import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import Keycloak from 'keycloak-js';
import api from '../services/api';

interface User {
  id: string;
  email?: string;
  name?: string;
  username?: string;
  roles: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.taegener.info',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'TT',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'learn-ui',
};

// Roles can be granted either via a Keycloak realm role or via the
// user_profiles.roles DB column (set from the admin "Nutzer"-Verwaltung).
// The backend already treats both as equivalent (see requireRole() in
// middleware/auth.ts) - mirror that here so the UI doesn't hide admin
// features from users who were only granted the DB role.
//
// Uses the shared `api` axios instance (correct baseURL, same as every
// other request) with an explicit Authorization header, since the
// interceptor's token getter is only wired up by App.tsx after this
// context has already produced a token.
async function fetchDbRoles(accessToken: string): Promise<string[]> {
  try {
    const response = await api.get('/me/profile', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return Array.isArray(response.data?.roles) ? response.data.roles : [];
  } catch (error) {
    console.error('Failed to load DB roles:', error);
    return [];
  }
}

const keycloak = new Keycloak(keycloakConfig);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    console.log('Keycloak config:', keycloakConfig);

    keycloak
      .init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        pkceMethod: 'S256',
        enableLogging: true,
      })
      .then(async (authenticated) => {
        console.log('Keycloak initialized successfully, authenticated:', authenticated);
        setIsAuthenticated(authenticated);
        if (authenticated && keycloak.tokenParsed) {
          const accessToken = keycloak.token || null;
          setToken(accessToken);

          const tokenRoles = keycloak.tokenParsed.realm_access?.roles || [];
          const dbRoles = accessToken ? await fetchDbRoles(accessToken) : [];

          setUser({
            id: keycloak.tokenParsed.sub || '',
            email: keycloak.tokenParsed.email,
            name: keycloak.tokenParsed.name,
            username: keycloak.tokenParsed.preferred_username,
            roles: Array.from(new Set([...tokenRoles, ...dbRoles])),
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Keycloak init failed:', error);
        console.error('Keycloak config was:', keycloakConfig);
        // Still allow the app to load, just without auth
        setIsLoading(false);
      });

    // Token refresh
    const refreshInterval = setInterval(() => {
      if (keycloak.authenticated) {
        keycloak.updateToken(60).then((refreshed) => {
          if (refreshed) {
            setToken(keycloak.token || null);
          }
        });
      }
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  const login = useCallback(() => {
    keycloak.login();
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const hasRole = useCallback(
    (role: string) => {
      return user?.roles.includes(role) || false;
    },
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        token,
        login,
        logout,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
